// Communication Module Constants

import {
  AudienceType,
  MessageType,
  AudienceGroup,
  MessageTemplate,
} from '../types/communication';

// ============================================
// AUDIENCE GROUPS
// ============================================

export const AUDIENCE_GROUPS: Record<'EVERYONE' | 'STUDENTS' | 'EMPLOYEES', AudienceGroup[]> = {
  EVERYONE: [
    {
      id: 'all_users',
      label: 'Everyone',
      desc: 'All students, staff & parents',
      icon: '🌐',
      type: AudienceType.ALL_USERS,
    },
  ],
  STUDENTS: [
    {
      id: 'all_students',
      label: 'All Students',
      desc: 'Broadcast to entire school',
      icon: '🎓',
      type: AudienceType.ALL_STUDENTS,
    },
    {
      id: 'class_wise',
      label: 'Class-Specific',
      desc: 'Target specific classes',
      icon: '📚',
      type: AudienceType.CLASS_WISE,
    },
    {
      id: 'individual_students',
      label: 'Individual Students',
      desc: 'Select specific students',
      icon: '👤',
      type: AudienceType.INDIVIDUAL_USERS,
    },
  ],
  EMPLOYEES: [
    {
      id: 'all_staff',
      label: 'All Staff',
      desc: 'Broadcast to all employees',
      icon: '👥',
      type: AudienceType.ALL_STAFF,
    },
    {
      id: 'role_wise',
      label: 'Role-Specific',
      desc: 'Target specific roles',
      icon: '🎭',
      type: AudienceType.ROLE_WISE,
    },
    {
      id: 'individual_staff',
      label: 'Individual Staff',
      desc: 'Select specific employees',
      icon: '👤',
      type: AudienceType.INDIVIDUAL_USERS,
    },
  ],
};

// ============================================
// ANNOUNCEMENT TEMPLATES (for quick announcements)
// ============================================

export const ANNOUNCEMENT_TEMPLATES = [
  {
    id: 'holiday',
    label: '🎉 Holiday Notice',
    content: 'School will remain closed tomorrow due to a public holiday. Regular classes will resume the day after.',
  },
  {
    id: 'exam',
    label: '📝 Exam Schedule',
    content: 'Mid-term examinations will commence from next Monday. Students are advised to prepare thoroughly and arrive on time.',
  },
  {
    id: 'event',
    label: '🎊 Upcoming Event',
    content: 'Our annual day celebration is scheduled for this Saturday at 9 AM in the school auditorium. All are welcome!',
  },
  {
    id: 'closure',
    label: '🚨 School Closure',
    content: 'Due to unforeseen circumstances, school will remain closed today. Further updates will be shared soon.',
  },
  {
    id: 'achievement',
    label: '🏆 Achievement',
    content: 'Congratulations to all students who participated in the inter-school competition. Our school secured first place!',
  },
  {
    id: 'meeting',
    label: '👥 Parent Meeting',
    content: 'A parent-teacher meeting is scheduled for this Friday at 3 PM. Your participation is highly encouraged.',
  },
];

// ============================================
// MESSAGE TEMPLATES
// ============================================

export const MESSAGE_TEMPLATES: MessageTemplate[] = [
  {
    id: 'homework',
    label: 'Homework Reminder',
    content: 'Please ensure your child completes the assigned homework and submits it on time. Thank you!',
    category: 'HOMEWORK',
  },
  {
    id: 'event',
    label: 'Event Notification',
    content: 'There will be a school event tomorrow. Please ensure your child attends on time.',
    category: 'EVENT',
  },
  {
    id: 'meeting',
    label: 'Parent Meeting',
    content: 'Dear Parent, there is a parent-teacher meeting scheduled. Your presence is requested.',
    category: 'GENERAL',
  },
  {
    id: 'absent',
    label: 'Absence Notice',
    content: 'Your child was absent today. Please send a leave application or contact the school.',
    category: 'REMINDER',
  },
  {
    id: 'urgent',
    label: 'Urgent Notice',
    content: 'This is an urgent notice. Please contact the school immediately regarding your child.',
    category: 'URGENT',
  },
  {
    id: 'achievement',
    label: 'Student Achievement',
    content: 'Congratulations! Your child has shown excellent performance and deserves appreciation.',
    category: 'GENERAL',
  },
];

// ============================================
// FEE REMINDER TEMPLATES
// ============================================

export const FEE_REMINDER_TEMPLATES = {
  STANDARD: 'Dear Parent, fee payment is pending. Please clear the dues at your earliest convenience to avoid inconvenience.',
  URGENT: 'URGENT: Your child\'s fee payment is overdue. Kindly clear the pending amount immediately to avoid late fees.',
  GENTLE: 'This is a gentle reminder that fee payment for this term is pending. Please pay at your convenience.',
  FINAL: 'Final Notice: Fee payment is long overdue. Please settle the outstanding amount to continue your child\'s education.',
};

export const DEFAULT_FEE_REMINDER = FEE_REMINDER_TEMPLATES.STANDARD;

// Mock recipient counts for "All" selections (fallback when API unavailable)
export const RECIPIENT_COUNTS = {
  ALL_USERS: 729,     // Students + Staff = estimated total
  ALL_STUDENTS: 640,  // Total students
  ALL_STAFF: 89,      // Total staff
};

// ============================================
// NOTIFICATION ICONS & COLORS
// ============================================

export const NOTIFICATION_CONFIG = {
  ANNOUNCEMENT: {
    icon: '📢',
    color: '#F59E0B', // Orange
    bgColor: '#FEF3C7', // Light orange
  },
  MESSAGE: {
    icon: '💬',
    color: '#3B82F6', // Blue
    bgColor: '#DBEAFE', // Light blue
  },
  FEE_REMINDER: {
    icon: '💰',
    color: '#EF4444', // Red
    bgColor: '#FEE2E2', // Light red
  },
  APPROVAL: {
    icon: '✅',
    color: '#10B981', // Green
    bgColor: '#D1FAE5', // Light green
  },
  EVENT: {
    icon: '📅',
    color: '#8A70D6', // Purple
    bgColor: '#EDE7F6', // Light purple
  },
  SYSTEM: {
    icon: '⚙️',
    color: '#6B7280', // Gray
    bgColor: '#F3F4F6', // Light gray
  },
  exam_schedule: {
    icon: '📝',
    color: '#8A70D6',
    bgColor: '#EDE7F6',
  },
  syllabus_pending: {
    icon: '📚',
    color: '#D97706',
    bgColor: '#FEF3C7',
  },
  scores_pending: {
    icon: '🏅',
    color: '#DC2626',
    bgColor: '#FEE2E2',
  },
};

// ============================================
// FEE FILTER PRESETS
// ============================================

export const FEE_FILTER_PRESETS = [
  {
    id: 'all',
    label: 'All Defaulters',
    minDue: 1,
    maxDue: undefined,
  },
  {
    id: 'high',
    label: 'Dues > ₹10k',
    minDue: 10000,
    maxDue: undefined,
  },
  {
    id: 'very_high',
    label: 'Dues > ₹20k',
    minDue: 20000,
    maxDue: undefined,
  },
  {
    id: 'medium',
    label: 'Dues ₹5k-₹10k',
    minDue: 5000,
    maxDue: 10000,
  },
  {
    id: 'low',
    label: 'Dues < ₹5k',
    minDue: 1,
    maxDue: 5000,
  },
];

// ============================================
// KEYBOARD SHORTCUTS
// ============================================

export const KEYBOARD_SHORTCUTS = {
  NEW_ANNOUNCEMENT: 'Ctrl+N',
  NEW_MESSAGE: 'Ctrl+M',
  FOCUS_FEE_SEARCH: 'Ctrl+F',
  OPEN_NOTIFICATIONS: 'Ctrl+Shift+N',
  SEND: 'Ctrl+Enter',
  CLOSE: 'Escape',
  GLOBAL_SEARCH: '/',
  HELP: '?',
};

// ============================================
// TIMEOUTS & INTERVALS
// ============================================

export const COMMUNICATION_CONFIG = {
  AUTO_SAVE_INTERVAL: 10000, // 10 seconds
  UNDO_TIMEOUT: 5000, // 5 seconds
  NOTIFICATION_POLL_INTERVAL: 30000, // 30 seconds
  TOAST_DURATION: 3000, // 3 seconds
  SUCCESS_MODAL_AUTO_CLOSE: 2000, // 2 seconds
};

// ============================================
// VALIDATION RULES
// ============================================

export const VALIDATION_RULES = {
  ANNOUNCEMENT_TITLE_MAX_LENGTH: 100,
  ANNOUNCEMENT_BODY_MAX_LENGTH: 500,
  MESSAGE_MAX_LENGTH: 1000,
  FEE_REMINDER_MAX_LENGTH: 300,
  MIN_RECIPIENTS: 1,
  MAX_RECIPIENTS: 1000,
};

// ============================================
// PAGINATION
// ============================================

export const PAGINATION_CONFIG = {
  NOTIFICATIONS_PER_PAGE: 10,
  HISTORY_PER_PAGE: 20,
  FEE_DEFAULTERS_PER_PAGE: 50,
};
